import { baseApi } from "./baseApi";
import { buildPaginationQueryString } from "./queryHelpers";
import { PaginateQuery } from "../../interfaces";
import {
  CreateEducationSystemRequest,
  EducationSystemDto,
} from "../../interfaces/curriculum";
import { MediaDto } from "../../interfaces/media";

export type UpdateEducationSystemRequest =
  Partial<CreateEducationSystemRequest>;

export const educationSystemApi = baseApi.injectEndpoints({
  endpoints: (builder) => ({
    getEducationSystems: builder.query<
      { data: EducationSystemDto[]; meta: any },
      PaginateQuery
    >({
      query: (data) => ({
        url: buildPaginationQueryString(data, "/api/education-system"),
      }),
      providesTags: ["Paginated-EducationSystems"],
    }),
    getEducationSystem: builder.query<EducationSystemDto, string>({
      query: (id) => ({ url: `/api/education-system/${id}` }),
      providesTags: ["EducationSystem"],
    }),
    getEducationSystemSelectList: builder.query<any, void>({
      query: () => ({ url: `/api/education-system/select-list/all` }),
      providesTags: ["EducationSystem-Select-List"],
    }),
    addEducationSystem: builder.mutation<any, CreateEducationSystemRequest>({
      query: (data) => ({
        url: "/api/education-system",
        method: "POST",
        body: data,
      }),
      invalidatesTags: [
        "EducationSystem",
        "EducationSystem-Select-List",
        "Paginated-EducationSystems",
      ],
    }),
    updateEducationSystem: builder.mutation<
      any,
      { id: string; data: UpdateEducationSystemRequest }
    >({
      query: ({ id, data }) => ({
        url: `/api/education-system/${id}`,
        method: "PATCH",
        body: data,
      }),
      invalidatesTags: [
        "EducationSystem",
        "EducationSystem-Select-List",
        "Paginated-EducationSystems",
      ],
    }),
    deleteEducationSystem: builder.mutation<any, string>({
      query: (id) => ({
        url: `/api/education-system/${id}`,
        method: "DELETE",
      }),
      invalidatesTags: [
        "EducationSystem-Select-List",
        "Paginated-EducationSystems",
      ],
    }),
    uploadEducationSystemIcon: builder.mutation<
      MediaDto,
      {
        file: File;
        educationSystemId?: string;
        onProgress?: (progress: number) => void;
      }
    >({
      query: ({ file, educationSystemId, onProgress }) => {
        const formData = new FormData();
        formData.append("file", file);

        return {
          url: educationSystemId
            ? `/api/education-system/upload/icon?id=${educationSystemId}`
            : `/api/education-system/upload/icon`,
          method: "POST",
          data: formData,
          onUploadProgress: (event: any) => {
            if (event.total && onProgress) {
              onProgress(Math.round((event.loaded / event.total) * 100));
            }
          },
        };
      },
      invalidatesTags: [
        "EducationSystem",
        "EducationSystem-Select-List",
        "Paginated-EducationSystems",
      ],
    }),
  }),
});

export const {
  useGetEducationSystemsQuery,
  useGetEducationSystemQuery,
  useGetEducationSystemSelectListQuery,
  useAddEducationSystemMutation,
  useUpdateEducationSystemMutation,
  useDeleteEducationSystemMutation,
  useUploadEducationSystemIconMutation,
} = educationSystemApi;
